import { useSceneStore } from '@/features/shared/stores/scene.store'
import { ToggleGroup, ToggleGroupItem } from '@/shared/components/ui/toggle-group'
import React, { useEffect, useState } from 'react'
import { BaseNode } from './nodes/BaseNode'
import { NodeType, lexicalNodes } from './nodes/NodeCore'
import { getCurrentNode, insertPageBreakOrNode } from './utils/NodeUtils'

export default function SpToolbar() {
    const { editor } = useSceneStore()
    const [selectedType, setSelectedType] = useState<NodeType | undefined>()
    const [displayToolbarShortcuts, setDisplayToolbarShortcuts] = useState(false)

    // Ctrl key toggling for showing shortcuts
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                setDisplayToolbarShortcuts(true)
            }
        }
        const handleKeyUp = (e: KeyboardEvent) => {
            if (e.key === 'Control') {
                setDisplayToolbarShortcuts(false)
            }
        }

        window.addEventListener('keydown', handleKeyDown)
        window.addEventListener('keyup', handleKeyUp)

        return () => {
            window.removeEventListener('keydown', handleKeyDown)
            window.removeEventListener('keyup', handleKeyUp)
        }
    }, [])

    // Listen for selection changes and update selectedType
    useEffect(() => {
        if (!editor) return

        return editor.registerUpdateListener(({ editorState }) => {
            editorState.read(() => {
                const currentNode = getCurrentNode()
                if (!currentNode) {
                    setSelectedType(undefined)
                    return
                }
                setSelectedType(currentNode.constructor.getType() as NodeType)
            })
        })
    }, [editor])

    if (!editor) return null

    return (
        <ToggleGroup
            type="single"
            value={selectedType}
            onValueChange={(value) => setSelectedType(value as NodeType)}
        >
            {lexicalNodes.map(([nodeType, node], index) => {
                const Icon = node.icon
                const isLast = index === lexicalNodes.length - 1
                return (
                    <React.Fragment key={nodeType}>
                        <ToggleGroupItem
                            key={nodeType}
                            value={nodeType}
                            className="data-[state=on]:bg-slate-200 data-[state=on]:rounded-lg"
                            aria-label={`Toggle ${node.label}`}
                            onClick={() => {
                                editor.update(() => {
                                    const newNode = BaseNode.create(node.constructor)
                                    const newNodeType = newNode.getType() as NodeType

                                    editor.getRootElement()?.focus()

                                    const currentNode = getCurrentNode()
                                    if (!currentNode) return
                                    insertPageBreakOrNode(newNodeType)

                                    setSelectedType(newNodeType)
                                })
                            }}
                        >
                            <div className="flex items-center px-0">
                                <div className="flex size-6 justify-center items-center pr-2">
                                    {displayToolbarShortcuts ? (
                                        <div className="bg-slate-800 text-white rounded-2xl w-5 h-5 px-1 text-xs flex items-center justify-center">
                                            {node.shortcut[1].length > 1
                                                ? node.shortcut[2]
                                                : node.shortcut[1]}
                                        </div>
                                    ) : (
                                        <Icon className="size-6 md:size-6 xl:size-8" />
                                    )}
                                </div>

                                <div className="h-4 px-0 hidden 2xl:block">{node.label}</div>
                            </div>
                        </ToggleGroupItem>
                        {!isLast && <div className="w-px h-8 bg-border mx-2" />}
                    </React.Fragment>
                )
            })}
        </ToggleGroup>
    )
}
